import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner, faClock, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import './TrainingStatusBanner.css';

export interface TrainingJob {
  job_id: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at?: string | null;
  completed_at?: string | null;
  training_examples_count: number;
  fine_tuned_model?: string | null;
  error_message?: string | null;
}

interface TrainingStatusBannerProps {
  activeJob: TrainingJob | null;
  lastUpdateTime: Date;
}

const TrainingStatusBanner: React.FC<TrainingStatusBannerProps> = ({ activeJob, lastUpdateTime }) => {
  if (!activeJob || activeJob.status === 'completed' || activeJob.status === 'failed' || activeJob.status === 'cancelled') {
    return null;
  }

  const getStatusConfig = () => {
    switch (activeJob.status) {
      case 'pending':
        return {
          icon: faClock,
          iconColor: '#f59e0b',
          bgColor: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderColor: '#f59e0b',
          title: 'Training Queued',
          message: 'Your training job is in the queue and will start shortly...',
          showSpinner: false,
          pulseColor: '#f59e0b'
        };
      case 'running':
        const startTime = activeJob.started_at ? new Date(activeJob.started_at) : new Date();
        const duration = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;

        // Estimate progress (rough estimate: 10 minutes for 10 examples, 30 minutes for 100+)
        const estimatedDuration = activeJob.training_examples_count <= 20 ? 600 : 1800; // seconds
        const progress = Math.min((duration / estimatedDuration) * 100, 95);

        return {
          icon: faSpinner,
          iconColor: '#4080F2',
          bgColor: 'linear-gradient(135deg, #DDEAFF 0%, #BBD4FF 100%)',
          borderColor: '#4080F2',
          title: 'Training in Progress',
          message: `Processing ${activeJob.training_examples_count} examples...`,
          duration: `${minutes > 0 ? `${minutes}m ` : ''}${seconds}s`,
          progress,
          showSpinner: true,
          pulseColor: '#4080F2'
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <div className="training-status-banner" style={{
      background: config.bgColor,
      borderLeft: `4px solid ${config.borderColor}`
    }}>
      <div className="status-content">
        <div className="status-icon">
          <FontAwesomeIcon
            icon={config.icon}
            spin={config.showSpinner}
            style={{ color: config.iconColor, fontSize: '20px' }}
          />
        </div>

        <div className="status-details">
          <div className="status-header">
            <h3>{config.title}</h3>
            {config.duration && (
              <span className="duration-badge">{config.duration}</span>
            )}
          </div>

          <p className="status-message">{config.message}</p>

          {config.progress !== undefined && (
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${config.progress}%`,
                    background: config.borderColor
                  }}
                >
                  <div className="progress-glow" />
                </div>
              </div>
              <span className="progress-text">{Math.round(config.progress)}%</span>
            </div>
          )}

          <div className="status-meta">
            {activeJob.job_id && (
              <span className="job-id">Job ID: {activeJob.job_id}</span>
            )}
            <span className="update-time">
              Last updated: {lastUpdateTime.toLocaleTimeString()}
            </span>
          </div>
        </div>

        <div className="status-pulse" style={{ background: config.pulseColor }} />
      </div>
    </div>
  );
};

export default TrainingStatusBanner;